import React, { useEffect, useState } from 'react';
import { attemptAppRedirect, detectMobileOS, generateAppLink } from '../../utils/deepLinkHandler';
import styled from 'styled-components';

const AppDetail: React.FC = () => {
  const [os, setOs] = useState<'ios' | 'android' | 'other'>('other');
  const [countdown, setCountdown] = useState(2);
  const [hasAttempted, setHasAttempted] = useState(false);

  useEffect(() => {
    // Only attempt once
    if (hasAttempted) return;

    const detectedOs = detectMobileOS();
    setOs(detectedOs);

    // Get the full path to pass to the app
    const currentPath = window.location.pathname + window.location.search;

    // Attempt to open the app immediately (only for mobile)
    if (detectedOs === 'ios' || detectedOs === 'android') {
      setHasAttempted(true);
      attemptAppRedirect(currentPath, 2500);

      // Start countdown (2 seconds to show UI before redirect)
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearInterval(countdownInterval);
      };
    }
  }, [hasAttempted]);

  const handleOpenApp = () => {
    // Try opening the app one more time with a shorter timeout
    const currentPath = window.location.pathname + window.location.search;
    attemptAppRedirect(currentPath, 1500);
  };

  const handleGoToStore = () => {
    // Go directly to the app store
    const appLinks = generateAppLink();

    if (os === 'ios') {
      window.location.href = appLinks.ios;
    } else if (os === 'android') {
      window.location.href = appLinks.android;
    }
  };

  // For desktop users
  if (os === 'other') {
    return (
      <Container>
        <Content>
          <Icon>📱</Icon>
          <Title>Mobile App Required</Title>
          <Description>Please download the Niyyah app from the app store to continue.</Description>
          <StoreLinks>
            <StoreLink
              href="https://apps.apple.com/app/guider-the-app-for-muslims/id6446500974"
              target="_blank">
              Download for iOS
            </StoreLink>
            <StoreLink
              href="https://play.google.com/store/apps/details?id=com.guider.guidermobile"
              target="_blank">
              Download for Android
            </StoreLink>
          </StoreLinks>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        {countdown > 0 ? (
          <>
            <Loader>
              <Spinner />
            </Loader>
            <Title>Opening Guider App...</Title>
            <Description>
              You&apos;ll be redirected to the app in {countdown} second
              {countdown !== 1 ? 's' : ''}
            </Description>
          </>
        ) : (
          <>
            <Icon>📱</Icon>
            <Title>Didn&apos;t open?</Title>
            <Description>
              The app should have opened automatically. If it didn&apos;t, you can try again or get
              the app from the store.
            </Description>
            <ButtonGroup>
              <PrimaryButton onClick={handleOpenApp}>Try Again</PrimaryButton>
              <SecondaryButton onClick={handleGoToStore}>Get the App</SecondaryButton>
            </ButtonGroup>
            <SmallText>Make sure you have the Guider app installed on your device</SmallText>
          </>
        )}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const Content = styled.div`
  text-align: center;
  background: white;
  padding: 48px 32px;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 100%;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
`;

const Spinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Icon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const Button = styled.button`
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  width: 100%;

  &:active {
    transform: scale(0.98);
  }
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;

  &:hover {
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
`;

const SecondaryButton = styled(Button)`
  background: white;
  color: #667eea;
  border: 2px solid #667eea;

  &:hover {
    background: #f8f9ff;
  }
`;

const StoreLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StoreLink = styled.a`
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  background: white;
  color: #667eea;
  border: 2px solid #667eea;
  transition: all 0.2s;
  display: inline-block;

  &:hover {
    background: #f8f9ff;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SmallText = styled.p`
  font-size: 14px;
  color: #999;
  margin-top: 16px;
`;

export default AppDetail;
